// KOA-5857 — Tri-state checkbox for nullable Boolean cells.
//
// Default boolean rendering collapses `null` and `false` to the same
// unchecked glyph, which is unsafe in a database tool: the user can't
// tell whether a row says "no" or "we don't know". This control
// renders the three states distinctly and lets the user cycle
// false → true → null → false with a click, so every state is
// reachable from every other state with at most two clicks.
//
// The null state renders as an italic "NULL" text label inside the
// box (instead of a generic dash) and switches the border to dashed
// — it reads instantly as "no value here", not "off" or "unchecked".
// The visual upgrade was the load-bearing fix: users were confusing
// the original dashed-fill state with a plain unchecked box.
//
// ARIA: role="checkbox" + aria-checked="true" | "false" | "mixed"
// (the W3C-blessed tri-state). Space and Enter advance the cycle the
// same way mouse click does.

import classNames from "classnames";
import React, { useCallback } from "react";

export type TriStateValue = boolean | null;

export interface ITriStateCheckboxProps {
  value: TriStateValue;
  disabled?: boolean;
  // Forward-declared so existing cells can drop the wrapper element's
  // onBlur straight through.
  onBlur?: () => void;
  onChange: (next: TriStateValue) => void;
}

const nextValue = (current: TriStateValue): TriStateValue => {
  // false → true → null → false. Cycling through `null` last means
  // users who only ever toggle on/off don't accidentally land in
  // "unknown"; they have to step past true to get there.
  if (current === false) {
    return true;
  }
  if (current === true) {
    return null;
  }
  return false;
};

const ariaCheckedFor = (value: TriStateValue): "true" | "false" | "mixed" => {
  if (value === true) {
    return "true";
  }
  if (value === false) {
    return "false";
  }
  return "mixed";
};

const titleFor = (value: TriStateValue): string => {
  if (value === true) {
    return "true — click for null";
  }
  if (value === false) {
    return "false — click for true";
  }
  return "null — click for false";
};

const TriStateCheckbox: React.FC<ITriStateCheckboxProps> = (props: ITriStateCheckboxProps): JSX.Element => {
  const { value, disabled, onBlur, onChange } = props;

  const advance = useCallback((): void => {
    onChange(nextValue(value));
  }, [value, onChange]);

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      advance();
    }
  };

  let stateClass: string;
  if (value === true) {
    stateClass = "is-true";
  } else if (value === false) {
    stateClass = "is-false";
  } else {
    stateClass = "is-null";
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={ariaCheckedFor(value)}
      className={classNames("kuika-tri-state", stateClass)}
      disabled={disabled}
      title={titleFor(value)}
      onClick={advance}
      onKeyDown={handleKey}
      onBlur={onBlur}
    >
      {value === true && <span className="kuika-tri-state__glyph">✓</span>}
      {/* Explicit text label — the earlier `−` glyph was being read
          as "off" or "muted false", defeating the whole point of a
          tri-state. "NULL" + the dashed border in CSS leaves no
          ambiguity that the cell holds no value at all. */}
      {value === null && <span className="kuika-tri-state__null-label">NULL</span>}
    </button>
  );
};

export default TriStateCheckbox;
